/* src/pages/Donation/index.jsx */

import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./index.scss";

// Imports from your project
import CustomStepper from "../../components/CustomStepper";
import CustomeStepperMobile from "../../components/CustomStepperMobile";
import CustomQuote from "../../components/CustomQuote";
import SelectCause from "./StepPages/SelectCause";
import SplitDonation from "./StepPages/SplitDonation";
import PaymentDetails from "./StepPages/PaymentDetails";
import Confirm from "./StepPages/Confirm";
import Successful from "./StepPages/Successful";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { backgroundVariants, pageVariants } from "../../assets/framer/styles";
import ShootingStars from "../../components/ShootingStars";
import FloatingClouds from "../../components/FloatingClouds";
import BirdAnimation from "../../components/BirdAnimation";
import { getDonationThemeConfig } from "../../utils/donationThemeConfig";

// i18n
import { useTranslation } from "react-i18next";

// Stripe setup
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, {
  locale: "en-CA"
});

const Donation = () => {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const template = searchParams.get("template") || "";
  const ref = searchParams.get("ref");
  const stage = parseInt(searchParams.get("stage") || "1", 10);
  const previousStage = useRef(stage);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    if (previousStage.current !== stage) {
      previousStage.current = stage;
    }
  }, [stage]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Step content logic
  const renderStepContent = () => {
    switch (stage) {
      case 1:
        return <SelectCause />;
      case 2:
        return <SplitDonation />;
      case 3:
        return <PaymentDetails />;
      case 4:
        return <Confirm />;
      case 5:
        return <Successful />;
      default:
        return <SelectCause />;
    }
  };

  // Direction for framer motion page transitions
  const direction = stage < previousStage.current ? -1 : 1;

  const {
    backgroundImage,
    quote,
    reference,
    quoteIcon,
    quoteAlt,
    cloudImages,
    showShootingStars,
    showClouds,
    showBirdAnimation
  } = getDonationThemeConfig({ template, ref, t });

  return (
    <div className="donatation-container">
      <motion.div
        style={{ backgroundImage: `url(${backgroundImage})` }}
        className="left-section"
        variants={backgroundVariants}
        initial="hidden"
        animate="visible"
      >
        {showShootingStars && <ShootingStars />}

        {showClouds && <FloatingClouds images={cloudImages} />}

        <CustomStepper />

        {showBirdAnimation && <BirdAnimation />}

        {/* Render the translated quote + reference */}
        <CustomQuote
          quote={quote}
          reference={reference}
          iconSrc={quoteIcon}
          iconAlt={quoteAlt}
        />
      </motion.div>

      <div
        className="right-section"
        style={
          windowWidth <= 950
            ? { backgroundImage: `url(${backgroundImage})` }
            : {}
        }
      >
        {windowWidth <= 950 &&
          showClouds && (
            <>
              <FloatingClouds images={cloudImages} />
            </>
          )}

        <div className="right-section-custom-stepper-mobile-container">
          <CustomeStepperMobile />
        </div>

        <div className="step-content-container">
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={stage}
              className="step-content"
              variants={pageVariants}
              initial="enter"
              animate="center"
              exit="exit"
              custom={direction}
            >
              <Elements stripe={stripePromise}>{renderStepContent()}</Elements>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Donation;
